import { type FC, useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { useQueryParams } from '@/support/Hooks/useQueryParams/useQueryParams';
import { _notNil } from '@/littledash';
import type { components } from '@/generated/internal-api/openapi-types';
import { NoDataMessage } from '@/components/NoDataCard/NoDataCard';
import Banner from '@/components/UI/Banner/Banner';
import Spinner from '@/components/UI/Spinner/Spinner';
import { Panel, Tab, Tabs } from '@/components/UI/Tabs';
import AgentCard from '@/components/Agents/AgentCard/AgentCard';
import { isAgentEnabled } from '@/components/Agents/AgentUtils';
import RecentSessionsPanel from '@/components/Agents/RecentSessionsPanel/RecentSessionsPanel';
import SessionsTable from '@/components/Agents/SessionsTable/SessionsTable';
import { featuresSelector } from '@/support/Selectors';
import { web as webRoute } from '@/support/route';
import type { State } from '@/model/State.model';
import type { AgentSessionSummaryV1 } from '@/model/Agent.model';

type AgentDefinitionV1 = components['schemas']['AgentDefinitionV1.schema'];
import { LuSparkle } from 'react-icons/lu';
import styles from './AgentsPage.module.scss';

const TAB_QUERY_PARAM = 'tab';
const TABS = ['agents', 'sessions'] as const;
const RECENT_SESSIONS_LIMIT = 5;

const getTabIndex = (params: URLSearchParams): number => {
  const tab = params.get(TAB_QUERY_PARAM);
  const index = TABS.indexOf(tab as (typeof TABS)[number]);
  return index >= 0 ? index : 0;
};

const AgentsPage: FC = () => {
  const navigate = useNavigate();
  const queryParams = useQueryParams();

  const currentUserApiId = useSelector((state: State) => state.user.currentUser?.api_id);
  const features = useSelector(featuresSelector);

  const { loading: agentsLoading, response: agentsResponse } = useApiHook({
    endpoint: 'GET /api/v1/ai-agents',
    options: {
      invokeOnInit: true,
      onError: {
        slug: 'agents-page',
        toast: false,
        throw: false,
      },
    },
  });

  const { loading: sessionsLoading, response: sessionsResponse } = useApiHook({
    endpoint: 'GET /api/v1/ai-agents/sessions',
    query: {
      perPage: RECENT_SESSIONS_LIMIT,
      'filters[]': _notNil(currentUserApiId) ? [`user|eq|${currentUserApiId}`] : [],
    },
    options: {
      invokeOnInit: true,
      onError: {
        slug: 'recent-sessions',
        toast: false,
        throw: false,
      },
    },
  });

  const loading = agentsLoading || sessionsLoading;
  const allAgents: Array<AgentDefinitionV1> = agentsResponse?.type === 'success' ? agentsResponse.body : [];
  const agents: Array<AgentDefinitionV1> = allAgents.filter((agent) => isAgentEnabled(features, agent.slug));
  const sessionsLoaded = sessionsResponse?.type === 'success';
  const sessions: Array<AgentSessionSummaryV1> = sessionsLoaded ? sessionsResponse.body.data : [];
  const [activeTab, setActiveTab] = useState(() => getTabIndex(queryParams));

  useEffect(() => {
    const tabFromUrl = getTabIndex(queryParams);
    if (tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl);
    }
  }, [queryParams]);

  useEffect(() => {
    const currentTab = queryParams.get(TAB_QUERY_PARAM);
    if (currentTab !== TABS[activeTab]) {
      const updatedParams = new URLSearchParams(queryParams.toString());
      updatedParams.set(TAB_QUERY_PARAM, TABS[activeTab]);
      navigate({ search: updatedParams.toString() });
    }
  }, [activeTab]);

  if (features.ai_agents !== true) {
    return <Navigate to={webRoute('dashboard')} replace />;
  }

  return (
    <div className="ph4 pb4" data-test-component="AgentsPage" data-test-element="container">
      <div className="pt3 pb4" data-test-element="header">
        <div className={styles.titleRow}>
          <h1 className="f3 normal ma0" data-test-element="title">
            In Vivo AI
          </h1>
          <LuSparkle size={20} className="blue" />
        </div>
        <p className="f5 lh-copy dark-gray mt3 mb0" data-test-element="subtitle">
          Purpose-built AI agents for your In Vivo studies. Each agent works with your live study data and returns
          results you can review and apply directly.
        </p>
      </div>

      <Banner info dismiss={false} className="mb4" testId="agents-beta-banner">
        <p className="f6 lh-copy ma0">
          In Vivo AI agents are currently in <strong>BETA</strong>. We are iterating on each agent based on feedback to
          optimize the experience and output. If you have any feedback, please contact{' '}
          <a className="blue" href="mailto:invivoai@benchling.com">
            invivoai@benchling.com
          </a>
          .
        </p>
      </Banner>

      <Tabs outerState={[activeTab, setActiveTab]}>
        <div className="bb b--moon-gray">
          <Tab className="f4" testId="agents-tab">
            Agents
          </Tab>
          <Tab className="f4" testId="sessions-tab">
            Sessions
          </Tab>
        </div>

        <Panel className="pt4">
          {loading ? (
            <div className="flex justify-center items-center w-100 pv5" data-testid="agents-page-loading">
              <Spinner size="large" color="blue" />
            </div>
          ) : (
            <div className={styles.content}>
              {agents.length > 0 ? (
                <div className={styles.grid}>
                  {agents.map((agent) => (
                    <AgentCard key={agent.api_id} agent={agent} />
                  ))}
                </div>
              ) : (
                <div className="w-100">
                  <NoDataMessage
                    title="No agents available"
                    text="AI agents will appear here when enabled for your team."
                    dark
                  />
                </div>
              )}
              <RecentSessionsPanel
                sessions={sessions}
                hasError={!sessionsLoaded}
                onViewAllSessions={() => setActiveTab(1)}
              />
            </div>
          )}
        </Panel>

        <Panel className="pt4">
          <SessionsTable agents={agents} />
        </Panel>
      </Tabs>
    </div>
  );
};

export default AgentsPage;
